import { useEffect, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';
import { columnsFor } from '../../lib/exploreColumns';
import type { ListEntityKind } from '../../types/list';

interface Props { kind: ListEntityKind; visible: string[]; onChange: (keys: string[]) => void; }

export function ColumnPicker({ kind, visible, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cols = columnsFor(kind);
  const toggle = (key: string) => {
    const next = visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key];
    if (next.length) onChange(next); // never empty
  };

  // Close on outside click (only while open)
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button type="button" className="btn-secondary text-sm flex items-center gap-1" onClick={() => setOpen((o) => !o)}>
        <Columns3 size={16} /> Columns
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 card p-2 w-56 max-h-80 overflow-auto">
          {cols.map((c) => (
            <label key={c.key} className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
              <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggle(c.key)} />
              <span className="text-gray-700 dark:text-gray-200">{c.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
